import { defineStore } from 'pinia';
import { type RouteLocationNormalizedGeneric, type RouteLocationNormalizedLoadedGeneric, type Router } from 'vue-router';

import { License, OrganizationFlags, ScheduleKind } from '../models';
import { useOrganizationStore } from './organizationStore';


const numbers = ['schedulableId'];

export const useUrlStore = defineStore('url', {
    state: () => ({
        campaignToggle: 'campaigns' as 'campaigns' | 'messages',
        loopToggle: 'loops' as 'loops' | 'media',
        playlistToggle: 'playlists' as 'playlists' | 'tracks',
        allowedToggles: [] as ('mine' | 'children' | 'shared')[],
        scopeToggle: 'mine' as 'mine' | 'children' | 'shared',
        allowedScheduleKinds: [] as ScheduleKind[],
        scheduleKind: ScheduleKind.MUSIC as ScheduleKind,
        schedulable: 'planning' as 'planning' | 'event',
        schedulableId: undefined as number | undefined
    }),
    actions: {
        reset(router: Router): void {
            const organizationStore = useOrganizationStore();

            this.allowedToggles = [];
            if (organizationStore.checkOrganizationFlags(OrganizationFlags.HAS_SHARED)) {
                this.allowedToggles.push('shared');
            }
            this.allowedToggles.push('mine');
            if (organizationStore.checkOrganizationFlags(OrganizationFlags.HAS_CHILDREN)) {
                this.allowedToggles.push('children');
            }

            this.allowedScheduleKinds = [];
            if (organizationStore.checkLicenses([License.SPOT_AUDIO])) {
                this.allowedScheduleKinds.push(ScheduleKind.MUSIC);
                this.allowedScheduleKinds.push(ScheduleKind.MESSAGE);
            }
            if (organizationStore.checkLicenses([License.SPOT_VIDEO])) {
                this.allowedScheduleKinds.push(ScheduleKind.VIDEO);
            }
            if (organizationStore.checkLicenses([License.SPOT_SCENT])) {
                this.allowedScheduleKinds.push(ScheduleKind.SCENT);
            }

            if (router.currentRoute.value.query.campaignToggle) {
                this.campaignToggle = router.currentRoute.value.query.campaignToggle as 'campaigns' | 'messages';
            }
            if (router.currentRoute.value.query.loopToggle) {
                this.loopToggle = router.currentRoute.value.query.loopToggle as 'loops' | 'media';
            }
            if (router.currentRoute.value.query.playlistToggle) {
                this.playlistToggle = router.currentRoute.value.query.playlistToggle as 'playlists' | 'tracks';
            }

            const changed: Record<string, any> = {};
            if (!this.allowedToggles.includes(this.scopeToggle)) {
                this.scopeToggle = 'mine';
                changed.scopeToggle = this.scopeToggle;
            }
            if (!this.allowedScheduleKinds.includes(this.scheduleKind)) {
                this.scheduleKind = this.allowedScheduleKinds[0] || ScheduleKind.MUSIC;
                this.schedulableId = undefined;
                changed.scheduleKind = this.scheduleKind;
                changed.schedulableId = undefined;
            }
            if (router.currentRoute.value.query.scopeToggle) {
                const scope = router.currentRoute.value.query.scopeToggle as 'mine' | 'children' | 'shared';
                if (this.allowedToggles.includes(scope)) {
                    this.scopeToggle = scope;
                    changed.scopeToggle = this.scopeToggle;
                }
            }
            if (router.currentRoute.value.query.scheduleKind) {
                const scheduleKind = router.currentRoute.value.query.scheduleKind as ScheduleKind;
                if (this.allowedScheduleKinds.includes(scheduleKind)) {
                    this.scheduleKind = scheduleKind;
                    changed.scheduleKind = this.scheduleKind;
                }
            }
            if (router.currentRoute.value.query.schedulable) {
                this.schedulable = router.currentRoute.value.query.schedulable as 'planning' | 'event';
                changed.schedulable = this.schedulable;
            }
            if (router.currentRoute.value.query.schedulableId) {
                const schedulableId = Number.parseInt(router.currentRoute.value.query.schedulableId as string);
                if (!Number.isNaN(schedulableId)) {
                    this.schedulableId = schedulableId;
                    changed.schedulableId = this.schedulableId;
                }
            }
            if (Object.keys(changed).length > 0) {
                router.replace({
                    query: {
                        ...router.currentRoute.value.query,
                        ...changed
                    }
                });
            }
        },
        updateQuery(to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric): boolean {
			let changed = false;
			for (const query of to.meta?.query as string[]) {
				if (to.query[query] == null) {
                    if (from.query[query] != null) {
                        to.query[query] = from.query[query] as any;
                        changed = true;
                    }
                    else if ((this as any)[query] != null) {
                        to.query[query] = (this as any)[query];
                        changed = true;
                    }
				}
                else {
                    if (to.query[query] !== (this as any)[query]) {
                        if (numbers.includes(query)) {
                            (this as any)[query] = Number.parseInt(to.query[query] as string);
                        }
                        else {
                            (this as any)[query] = to.query[query];
                        }
                    }
                }
			}
            return changed;
        },
        setCampaignToggle(router: Router, campaignToggle: 'campaigns' | 'messages'): void {
            this.campaignToggle = campaignToggle;
            router.replace({
                query: {
                    ...router.currentRoute.value.query,
                    campaignToggle: campaignToggle
                }
            });
        },
        setLoopToggle(router: Router, loopToggle: 'loops' | 'media'): void {
            this.loopToggle = loopToggle;
            router.replace({
                query: {
                    ...router.currentRoute.value.query,
                    loopToggle: loopToggle
                }
            });
        },
        setPlaylistToggle(router: Router, playlistToggle: 'playlists' | 'tracks'): void {
            this.playlistToggle = playlistToggle;
            router.replace({
                query: {
                    ...router.currentRoute.value.query,
                    playlistToggle: playlistToggle
                }
            });
        },
        setScopeToggle(router: Router, scopeToggle: 'mine' | 'children' | 'shared'): void {
            if (!this.allowedToggles.includes(scopeToggle)) {
                return;
            }
            this.scopeToggle = scopeToggle;
            router.replace({
                query: {
                    ...router.currentRoute.value.query,
                    scopeToggle: scopeToggle
                }
            });
        },
        setSchedulable(router: Router, scheduleKind: ScheduleKind, schedulable: 'planning' | 'event', schedulableId?: number): void {
            if (!this.allowedScheduleKinds.includes(scheduleKind)) {
                return;
            }
            this.scheduleKind = scheduleKind;
            this.schedulable = schedulable;
            this.schedulableId = schedulableId;
            router.replace({
                query: {
                    ...router.currentRoute.value.query,
                    scheduleKind: scheduleKind,
                    schedulable: schedulable,
                    schedulableId: schedulableId?.toString()
                }
            });
        }
    }
});